import {z} from "zod"

import {assertOrNotFound, notFound} from "@frontend/utils/notFound"
import {trpc} from "@frontend/utils/trpc"
import {useStabilizedValue} from "@frontend/utils/useStabilizedValue"
import {
  useTypedParams,
  useTypedSearchParams,
} from "@frontend/utils/useTypedParams"
import {id} from "@frontend/utils/zod"
import type {
  RemediationPageData,
  RemediationPageDataRemediation,
} from "@ri2/app/server/routers/getRemediationPageData"

import {CaseIdParams} from "../../params"

export const CauseIdParams = z
  .object({
    causeOnMessageId: z.union([id, z.literal("peek")]),
  })
  .merge(CaseIdParams)

export const SearchParams = z.object({
  recommendation: z
    .union([
      z.literal("replaceRemediation"),
      z.literal("difmRemediation"),
      z.literal("diyRemediation"),
    ])
    .optional(),
})

export const useDataFromParams = (): {
  causeOnMessageId: number | "peek"
  caseId: string
  remediationPageData: RemediationPageData
} => {
  const {causeOnMessageId, caseId} = useTypedParams(CauseIdParams)

  let [remediationPageData] = trpc.remediationPageData.useSuspenseQuery({
    caseId,
    causeOnMessageId,
  })

  assertOrNotFound(remediationPageData)

  const searchParams = useTypedSearchParams(SearchParams, {})

  if (searchParams.recommendation) {
    remediationPageData = {
      ...remediationPageData,
      recommendation: {
        reasons: ["skillLevel"],
        type: searchParams.recommendation,
      },
      remediationData: remediationPageData.remediationData.map((data) => ({
        ...data,
        isRecommended: data.type === searchParams.recommendation,
      })),
    }
  }

  return useStabilizedValue({
    causeOnMessageId,
    caseId,
    remediationPageData,
  })
}

export const getRemediationTypeRemediationPageDataOrNotFound = (
  remediationPageData: RemediationPageData,
): RemediationPageDataRemediation => {
  if (remediationPageData.type.type !== "remediation") {
    notFound()

    // This is just to satisfy TS. `notFound` throws, so this won't be
    // reached.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return null as any as RemediationPageDataRemediation
  }

  return remediationPageData.type
}
